import {COLORS} from 'frontend/utils/color_palettes';
import {MapWithDefault} from 'frontend/utils/common';

import {ElectricalType} from '../model/model_analytics';
import {VoltageLevels} from '../model/voltage_levels';
import {AssetIcon} from './icons/default';
import {LineProperties} from './lines_factory';
import {AssetId, AssetState, MapAsset} from './map_asset';
import {AssetMapLine} from './map_lines';
import {AssetMapMarker, IconSize} from './map_markers/default';
import {RenderedObjectPool} from './rendered_shape_pool';

/** A shape that Google Maps can render.*/
export type AssetShape = AssetMapLine | AssetMapMarker;
export type IconsFactory = (asset: MapAsset) => AssetIcon | null;
export type LinesFactory = (asset: MapAsset, state: AssetState, hidden: boolean) => LineProperties;

type DeckGLPoint = [number, number];
export type DeckGLColor = [number, number, number, number];

export interface DeckGLIcon {
  url: string;
  width: number;
  height: number;
}

export interface DeckGLIconAsset {
  id: AssetId;
  icon: DeckGLIcon;
  size: IconSize;
  position: DeckGLPoint;
  zIndex?: number;
}

export interface DeckGLPathAsset {
  id: AssetId;
  path: DeckGLPoint[];
  width: number;
  color: DeckGLColor;
  zIndex?: number;
}

// Corresponds to the color black.
export const DEFAULT_POLYLINE_STROKE_COLOR_BLACK = '#000000FF';

export const LINE_COLOR_BY_VOLTAGE_LEVEL = new MapWithDefault<VoltageLevels, string>(
  [
    [VoltageLevels.EXACT_765, COLORS.Blue.get(800)],
    [VoltageLevels.RANGE_345_TO_550, DEFAULT_POLYLINE_STROKE_COLOR_BLACK],
    [VoltageLevels.EXACT_345, COLORS.Orange.get(900)],
    [VoltageLevels.EXACT_220, COLORS.Green.get(500)],
    [VoltageLevels.EXACT_154, COLORS.Red.get(500)],
    [VoltageLevels.EXACT_138, COLORS.Purple.get(500)],
    [VoltageLevels.EXACT_110, COLORS.Orange.get(500)],
    [VoltageLevels.EXACT_69, COLORS.Blue.get(500)],
    [VoltageLevels.EXACT_66, COLORS.Magenta.get(200)],
    [VoltageLevels.RANGE_23_TO_44, COLORS.Yellow.get(500)],
    [VoltageLevels.RANGE_4_TO_35, DEFAULT_POLYLINE_STROKE_COLOR_BLACK],
  ],
  DEFAULT_POLYLINE_STROKE_COLOR_BLACK,
);

/**
 * Defines a generic interface for creating Google Maps shapes from an Asset.
 */
export interface AssetShapeFactory {
  create(asset: MapAsset, highlight: boolean, map: google.maps.Map): AssetShape | null;

  setAssetsPool(assetsPool: RenderedObjectPool<MapAsset, string>): void;

  selectAsset(assetId: AssetId | null): void;

  onZoomChanged?(level: number): void;
}

export interface DeckGLAssetShapeFactory {
  readonly pathTypes: Set<ElectricalType>;

  assetToDeckGLPath(asset: MapAsset, state: AssetState, hidden: boolean): DeckGLPathAsset;
  assetToDeckGLIcon(asset: MapAsset, state: AssetState): DeckGLIconAsset | null;
}
